'use client'

import dynamic from 'next/dynamic'
import { type ComponentType, type ReactNode, use } from 'react'
import { DEFAULT_LOCALE, type SupportedLocale } from '@/i18n/locales'
import { getWorkspaceMessages } from '@/lib/i18n/workspace-messages'
import { ScopedIntlProvider } from './scoped-intl-provider'

function CatalogLoaded() {
  return null
}
const catalogLoaded = () => CatalogLoaded

// The catalog is a versioned chunk instead of part of every HTML/RSC response,
// but a bare import() is only discovered when hydration reaches use() below.
// next/dynamic makes the server render emit a preload for the chunk it used,
// so the browser fetches the active locale's catalog with the route's
// JavaScript. Keep each import() literal: the compiler reads it to find the
// chunk, which is the same one getWorkspaceMessages loads.
// The server render needs only that preload, so it skips the import and keeps
// the compiled catalogs out of every server function.
// Dev skips the preload: resolving a chunk for every dynamic() target compiled all 15
// catalogs (~80 MB of JSON) into each workspace route's first dev compile.
const serverCatalog = () => Promise.resolve(CatalogLoaded)
const catalogPreloads: Partial<Record<SupportedLocale, ComponentType>> =
  process.env.NODE_ENV === 'development'
    ? {}
    : {
        en: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/en.json').then(catalogLoaded)
        ),
        zh: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/zh.json').then(catalogLoaded)
        ),
        hi: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/hi.json').then(catalogLoaded)
        ),
        es: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/es.json').then(catalogLoaded)
        ),
        fr: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/fr.json').then(catalogLoaded)
        ),
        ar: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/ar.json').then(catalogLoaded)
        ),
        bn: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/bn.json').then(catalogLoaded)
        ),
        pt: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/pt.json').then(catalogLoaded)
        ),
        ru: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/ru.json').then(catalogLoaded)
        ),
        ja: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/ja.json').then(catalogLoaded)
        ),
        it: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/it.json').then(catalogLoaded)
        ),
        he: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/he.json').then(catalogLoaded)
        ),
        de: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/de.json').then(catalogLoaded)
        ),
        el: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/el.json').then(catalogLoaded)
        ),
        pl: dynamic(() =>
          typeof window === 'undefined'
            ? serverCatalog()
            : import('../../../messages/pl.json').then(catalogLoaded)
        ),
      }

export function WorkspaceMessages({
  locale,
  namespaces,
  children,
}: {
  locale: string
  namespaces?: readonly string[]
  children: ReactNode
}) {
  const messages = use(getWorkspaceMessages(locale, namespaces))
  return (
    <ScopedIntlProvider locale={locale} messages={messages}>
      {children}
    </ScopedIntlProvider>
  )
}

export function WorkspaceIntlProvider({
  locale,
  children,
}: {
  locale: string
  children: ReactNode
}) {
  // A sibling, not a wrapper: a language switch swaps this element without
  // remounting the workspace below it.
  const CatalogPreload = Object.hasOwn(catalogPreloads, locale)
    ? catalogPreloads[locale as SupportedLocale]
    : catalogPreloads[DEFAULT_LOCALE]
  return (
    <>
      {CatalogPreload ? <CatalogPreload /> : null}
      <WorkspaceMessages locale={locale}>{children}</WorkspaceMessages>
    </>
  )
}
